import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService, ImpersonationInfo } from '../../../services/auth/auth';
import { LanguageService } from '../../../services/i18n/language';

/**
 * Плашка «Вы действуете как …» для режима «Войти как» (администратор работает от имени клиента).
 * Закреплена внизу экрана на любой странице, показывает сколько осталось и возвращает в админскую сессию.
 * Когда 30-минутный токен истёк, сама возвращает админа.
 */
@Component({
  selector: 'app-impersonation-banner',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (info) {
      <div class="imp-banner" role="status">
        <span class="imp-banner__text">
          {{ t('impersonation.banner', 'Вы действуете как {name}', { name: info.targetName }) }}
          <span class="imp-banner__time">· {{ remaining }}</span>
        </span>
        <button type="button" class="imp-banner__exit" (click)="exit()">
          {{ t('impersonation.exit', 'Вернуться в админку') }}
        </button>
      </div>
    }
  `,
  styles: [`
    .imp-banner {
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      min-height: 48px;
      padding: 8px 16px;
      background: #b45309;
      color: #fff;
      font-size: 14px;
      font-weight: 600;
      box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.25);
    }
    .imp-banner__time { font-weight: 500; opacity: 0.9; font-variant-numeric: tabular-nums; }
    .imp-banner__exit {
      flex: none;
      padding: 8px 14px;
      border: 0;
      border-radius: 8px;
      background: #fff;
      color: #92400e;
      font: inherit;
      font-weight: 700;
      cursor: pointer;
    }
    .imp-banner__exit:focus-visible { outline: 3px solid #fde68a; outline-offset: 2px; }
  `]
})
export class ImpersonationBannerComponent implements OnInit, OnDestroy {
  info: ImpersonationInfo | null = null;
  remaining = '';

  private timer?: ReturnType<typeof setInterval>;
  private paddedBody = false;

  constructor(
    private authService: AuthService,
    private languageService: LanguageService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.refresh();
    // Раз в секунду: вход/выход из режима может произойти на другой странице, плюс обратный отсчёт
    this.timer = setInterval(() => this.refresh(), 1000);
  }

  ngOnDestroy(): void {
    if (this.timer) {
      clearInterval(this.timer);
    }
    this.setBodyPadding(false);
  }

  t(key: string, fallback: string, params?: { [key: string]: string | number }): string {
    const value = this.languageService.translate(key, params);
    if (value && value !== key) {
      return value;
    }
    return params ? Object.keys(params).reduce((text, k) => text.replace(`{${k}}`, String(params[k])), fallback) : fallback;
  }

  exit(): void {
    if (this.authService.stopImpersonation()) {
      this.router.navigate(['/admin/users']);
    } else {
      this.router.navigate(['/login']);
    }
    this.refresh();
  }

  private refresh(): void {
    this.info = this.authService.getImpersonation();
    this.setBodyPadding(this.info !== null);
    if (!this.info) {
      return;
    }
    const left = new Date(this.info.expiresAt).getTime() - Date.now();
    if (left <= 0) {
      this.exit();
      return;
    }
    const totalSeconds = Math.floor(left / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    this.remaining = `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  /** Чтобы плашка не закрывала низ страницы (кнопки, футер). */
  private setBodyPadding(on: boolean): void {
    if (on === this.paddedBody) {
      return;
    }
    this.paddedBody = on;
    document.body.style.paddingBottom = on ? '56px' : '';
  }
}
